import { toFieldCategory } from '@atlassian/jira-common-rest/src/internal/simplified/1.0/projects/id/issuetypes/id/types.tsx';
import { gqlTagGira } from '@atlassian/jira-issue-fetch-services-common/src/services/issue-gira-data/gira-fragments/index.tsx';
import { getClient } from '@atlassian/jira-issue-fetch-services/src/common/utils/client.tsx';
import { fetchProformaReqTypeConfig } from '@atlassian/jira-proforma-request-type-config/src/services/fetch-proforma-rtc/index.tsx';
import {
	toErrorId,
	toErrorMessage,
} from '@atlassian/jira-project-settings-apps-common/src/types.tsx';
import { famaCatchAndLogFetchError } from '@atlassian/jira-project-settings-apps-common/src/util/catch-and-log-fetch-error.tsx';
import { fetchExplicitFieldAssociation } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/services/fetch-explicit-field-association/index.tsx';
import {
	AvailableLayoutItemGroupsFragments,
	JiraIssueLayoutItemConfigurationFragment,
	JiraIssueLayoutItemContainerFragment,
	JiraIssueLayoutUsageInfoFragment,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/services/fragments/index.tsx';
import { createLayoutItemsIndex } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/services/transformer/active-layout-items.tsx';
import { transformAvailableLayoutItemGroups } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/services/transformer/available-layout-item-groups.tsx';
import { transformLayoutOwners } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/services/transformer/layout-owners.tsx';
import platformTransformers from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/services/transformer/platform-transformers.tsx';
import {
	type FetchLayoutConfigurationServiceParams,
	type FetchLayoutConfigurationServiceResponse,
	type IssueRestrictionType,
	ExcludeFromMonitoringError,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types.tsx';
import {
	type IssueLayoutOwner,
	type ExtraDefinerId,
	IssueLayoutRequestTypeOwnerType,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/issue/issue-layout-owner.tsx';
import type { IssueTypeProperties } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/issue/issue-type-properties.tsx';
import {
	type FieldCategoryConfiguration,
	type FieldTypeObj,
	toFieldType,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/layout-items.tsx';
import { is404GraphqlResponse } from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/utils/error-response-checker/index.tsx';
import type { ProjectContext } from '@atlassian/jira-router-resources-project-context/src/services/project-context/types.tsx';
import { toBaseUrl } from '@atlassian/jira-shared-types/src/general.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	JSD_FIELD_ALIAS,
	JSD_FIELD_HELPTEXT,
	JSD_IS_EMAIL_REQUEST_TYPE,
} from '../../common/constants.tsx';
import {
	jsmTmpTransformActiveLayoutItem,
	jsmTmpTransformActiveLayoutItemForEmailRequest,
} from '../../common/utils/transformer/active-layout-item.tsx';
import { jsmTmpTransformCategory } from '../../common/utils/transformer/category.tsx';
import { PACKAGE_NAME } from '../../constants.tsx';
import type { TMPJSMLayoutConfigurationFragment } from './__generated_apollo__/TMPJSMLayoutConfigurationFragment';

type GraphqlResponse<T> = {
	data?: T;
	errors?: string[];
};

const LayoutConfigurationFragment = gqlTagGira`
${JiraIssueLayoutUsageInfoFragment}
${JiraIssueLayoutItemContainerFragment}
${AvailableLayoutItemGroupsFragments}
${JiraIssueLayoutItemConfigurationFragment}
fragment TMPJSMLayoutConfigurationFragment on Query {
  simplifiedProject(projectId: $projectId) {
    issueType(id: $extraDefinerId) {
      properties(keys: $requestOwnerPropertyKeys)
      issueRestriction {
        defaultRestrictions {
          restrictionValue
        }
      }
    }
  }
  projectConfiguration(projectId: $projectId) {
    ... on JiraProjectConfigurationResult {
      limitInfo {
        limitFieldsPerProject
        currentFieldsPerProject
        limitOptionsPerField
      }
    }
  }
  issueLayoutConfiguration(issueLayoutKey: {projectId: $projectId, extraDefinerId: $extraDefinerId}, type: ISSUE_VIEW) {
    __typename
    ... on JiraIssueLayoutConfigurationResult {
      issueLayoutResult {
        __typename
        id
        name
        usageInfo {
           ...JiraIssueLayoutUsageInfo
        }
        containers {
            ...JiraIssueLayoutItemContainerFragment
        }
      }
      metadata {
        __typename
        configuration {
            ...JiraIssueLayoutItemConfigurationFragment
        }
        availableFieldTypes {
          nodes {
            name
            key
            operations {
              editable
              canModifyRequired
              canModifyOptions
              canModifyDefaultValue
              canModifyPropertyConfiguration
              removable
              deletable
              canAssociateInSettings
              categoriesWhitelist
            }
          }
        }
        availableItems {
          restrictedFields(first: $availableItemsPageSize) {
            ...CustomAvailableLayoutItemsGroup
          }
          suggestedFields(first: $availableItemsPageSize) {
            ...CustomAvailableLayoutItemsGroup
          }
          systemAndAppFields(first: $availableItemsPageSize) {
            ...SystemAvailableLayoutItemsGroup
          }
          textFields(first: $availableItemsPageSize) {
            ...CustomAvailableLayoutItemsGroup
          }
          labelsFields(first: $availableItemsPageSize) {
            ...CustomAvailableLayoutItemsGroup
          }
          peopleFields(first: $availableItemsPageSize) {
            ...CustomAvailableLayoutItemsGroup
          }
          dateFields(first: $availableItemsPageSize) {
            ...CustomAvailableLayoutItemsGroup
          }
          selectFields(first: $availableItemsPageSize) {
            ...CustomAvailableLayoutItemsGroup
          }
          numberFields(first: $availableItemsPageSize) {
            ...CustomAvailableLayoutItemsGroup
          }
          otherFields(first: $availableItemsPageSize) {
            ...CustomAvailableLayoutItemsGroup
          }
          advancedFields(first: $availableItemsPageSize) {
            ...CustomAvailableLayoutItemsGroup
          }
        }
      }
    }
  }
}
`;

const fetchIssueLayoutConfigurationInternal = (
	projectContext: ProjectContext,
	extraDefinerId: ExtraDefinerId,
	availableItemsPageSize: number,
): Promise<GraphqlResponse<TMPJSMLayoutConfigurationFragment>> => {
	const client = getClient(toBaseUrl('')); // in cloud, baseUrl is always empty string
	const { projectId } = projectContext;
	// remove the $isNumberFieldConfigurableEnabled argument from the query when cleaning up the FG frontier-currency-field-d1
	const query = `
            query SwiftJsmTmpInitial($projectId: Long!, $extraDefinerId: Long!, $fieldPropertyKeys: [String!]!, $availableItemsPageSize: Int!, $requestOwnerPropertyKeys: [String!] = [], $isNumberFieldConfigurableEnabled: Boolean!) {
                ...TMPJSMLayoutConfigurationFragment
            }
            ${LayoutConfigurationFragment}
    `;

	return client.query({
		query,
		variables: {
			projectId,
			extraDefinerId,
			fieldPropertyKeys: [
				JSD_FIELD_ALIAS,
				JSD_FIELD_HELPTEXT,
				'jsd.field.approval.config',
				'jsd.field.approval.all.configs',
			],
			requestOwnerPropertyKeys: [JSD_IS_EMAIL_REQUEST_TYPE],
			availableItemsPageSize,
			isNumberFieldConfigurableEnabled: fg('frontier-currency-field-jsm-tmp'),
		},
	});
};

type FetchLayoutConfigurationServiceResponseWithoutEFA = Omit<
	FetchLayoutConfigurationServiceResponse,
	'explicitFieldAssociation'
>;

const responseTransformer = (
	extraDefinerId: ExtraDefinerId,
	response: GraphqlResponse<TMPJSMLayoutConfigurationFragment>,
	defaultCategoryConfigurations: FieldCategoryConfiguration[],
	emailRequestTypeCategoryConfigurations: FieldCategoryConfiguration[],
): FetchLayoutConfigurationServiceResponseWithoutEFA => {
	if (response.errors && response.errors.length > 0) {
		if (is404GraphqlResponse(response.errors)) {
			throw new ExcludeFromMonitoringError('Failed to load initial data: 404');
		}
		throw new Error(
			`Failed to load initial data. issue-type-page.services.fetch-data-service${JSON.stringify(
				response.errors,
			)}`,
		);
	}

	if (!response.data) {
		throw new Error('Failed to load initial data. `response.data` is empty');
	}

	const { issueLayoutConfiguration, simplifiedProject, projectConfiguration } = response.data;

	if (
		!simplifiedProject ||
		!simplifiedProject.issueType ||
		!issueLayoutConfiguration ||
		issueLayoutConfiguration.__typename === 'QueryError' ||
		!issueLayoutConfiguration.issueLayoutResult ||
		issueLayoutConfiguration.issueLayoutResult.name === null ||
		issueLayoutConfiguration.issueLayoutResult.name === undefined ||
		!issueLayoutConfiguration.issueLayoutResult.containers ||
		!issueLayoutConfiguration.issueLayoutResult.usageInfo ||
		!issueLayoutConfiguration.issueLayoutResult.usageInfo.edges ||
		// @ts-expect-error - TS2367 - This condition will always return 'false' since the types '"JiraIssueLayoutResult"' and '"QueryError"' have no overlap.
		issueLayoutConfiguration.issueLayoutResult.__typename === 'QueryError' ||
		!issueLayoutConfiguration.metadata ||
		// @ts-expect-error - TS2367 - This condition will always return 'false' since the types '"JiraIssueLayoutMetadata"' and '"QueryError"' have no overlap.
		issueLayoutConfiguration.metadata.__typename === 'QueryError' ||
		!projectConfiguration ||
		projectConfiguration.__typename === 'QueryError' ||
		!projectConfiguration.limitInfo
	) {
		throw new Error('Failed to load initial data. `issueLayoutConfiguration` has the wrong shape');
	}

	const gqlIssueRestriction = simplifiedProject.issueType.issueRestriction;
	const properties: IssueTypeProperties | undefined =
		simplifiedProject.issueType.properties || undefined;

	const isEmailRequestType = properties && properties[JSD_IS_EMAIL_REQUEST_TYPE];

	const jsmTmpTransformers = {
		...platformTransformers,
		activeLayoutItem: isEmailRequestType
			? jsmTmpTransformActiveLayoutItemForEmailRequest
			: jsmTmpTransformActiveLayoutItem,
		category: jsmTmpTransformCategory,
	};

	let issueRestriction: IssueRestrictionType | null = null;
	if (gqlIssueRestriction && gqlIssueRestriction.defaultRestrictions) {
		issueRestriction = {
			defaultRestrictions: gqlIssueRestriction.defaultRestrictions.map(({ restrictionValue }) => ({
				restrictionValue,
			})),
		};
	}

	const {
		issueLayoutResult: {
			id: layoutId,
			name: screenName,
			usageInfo: { edges: layoutOwnersEdges },
			containers,
		},
		metadata: {
			configuration: {
				items: { nodes: layoutItemNodes },
			},
			availableItems,
			availableFieldTypes,
		},
	} = issueLayoutConfiguration;

	if (containers.length === 0) {
		throw new Error(`Failed to load initial data. No containers for issueLayout ${extraDefinerId}`);
	}

	const layoutOwnerEdge = layoutOwnersEdges.find(({ currentProject }) => currentProject);

	if (!layoutOwnerEdge || !layoutOwnerEdge.node || !layoutOwnerEdge.node.layoutOwners) {
		throw new Error(
			`Failed to load initial data. No layoutOwners for issueLayout ${extraDefinerId}`,
		);
	}

	const {
		node: { layoutOwners },
	} = layoutOwnerEdge;

	const availableLayoutItemGroups = transformAvailableLayoutItemGroups(availableItems || {});
	const { fieldsById, panelsById } = createLayoutItemsIndex(layoutItemNodes);

	const activeLayoutItems = jsmTmpTransformers.activeLayoutItems(
		containers,
		fieldsById,
		panelsById,
		jsmTmpTransformers,
	);

	const fieldTypes = availableFieldTypes.nodes.map(
		({ name, key, operations: { __typename, categoriesWhitelist, ...restOperations } }) =>
			// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
			({
				name,
				key: toFieldType(key),
				operations: {
					...restOperations,
					categoriesWhitelist: categoriesWhitelist.map(toFieldCategory),
				},
			}) as FieldTypeObj,
	);

	const { currentFieldsPerProject, limitFieldsPerProject, limitOptionsPerField } =
		projectConfiguration.limitInfo;
	const issueLayoutOwners: IssueLayoutOwner[] = transformLayoutOwners(layoutOwners).map((owner) => {
		if (owner.data.id === extraDefinerId && owner.type === IssueLayoutRequestTypeOwnerType) {
			return {
				...owner,
				data: {
					...owner.data,
					properties: { ...owner.data.properties, ...properties },
				},
			};
		}

		return owner;
	});

	return {
		layoutId: layoutId ?? null,
		screenName,
		issueLayoutOwners,
		fieldTypes,
		categoryConfigurations: isEmailRequestType
			? emailRequestTypeCategoryConfigurations
			: defaultCategoryConfigurations,
		tabs: jsmTmpTransformers.tabs(containers, fieldsById, jsmTmpTransformers),
		activeLayoutItems,
		availableLayoutItemGroups,
		// TODO: add non-configurable fields information
		nonConfigurableFields: [],
		issueRestriction,
		// limits
		projectFieldCollection: {
			limitProjectScopedPerProject: null,
			limitFieldsPerProject,
			currentFieldsPerProject,
			nextFieldsPerProject: currentFieldsPerProject,
		},
		fieldOptionCollection: { limitPerField: limitOptionsPerField },
		affectedProjects: [],
		limits: {
			optionsPerField: limitOptionsPerField,
		},
		allowedActions: null,
		accessControl: null,
	};
};

export const fetchLayoutConfiguration =
	(
		defaultCategoryConfigurations: FieldCategoryConfiguration[],
		emailRequestTypeCategoryConfigurations: FieldCategoryConfiguration[],
	) =>
	async ({
		projectContext,
		extraDefinerId,
		availableItemsPageSize,
		cloudId,
	}: FetchLayoutConfigurationServiceParams): Promise<FetchLayoutConfigurationServiceResponse> => {
		const [layoutConfiguration, proformaReqTypeConfig, explicitFieldAssociation] =
			await Promise.all([
				famaCatchAndLogFetchError<GraphqlResponse<TMPJSMLayoutConfigurationFragment>>(
					fetchIssueLayoutConfigurationInternal(
						projectContext,
						extraDefinerId,
						availableItemsPageSize,
					),
					PACKAGE_NAME,
					toErrorId('fetchSwiftConfigurationData'),
					toErrorMessage('Failed to fetch SWIFT configuration data for the issue layout'),
				).then((response) =>
					responseTransformer(
						extraDefinerId,
						response,
						defaultCategoryConfigurations,
						emailRequestTypeCategoryConfigurations,
					),
				),
				fetchProformaReqTypeConfig({
					projectId: projectContext.projectId,
					requestTypeId: extraDefinerId,
					cloudId,
				}),
				fetchExplicitFieldAssociation(projectContext.projectId),
			]);

		return {
			...layoutConfiguration,
			...(proformaReqTypeConfig ? { proformaReqTypeConfig } : {}),
			explicitFieldAssociation,
		};
	};
